/**
 * TaskPerformancesListPanel
 *
 * 성과 목록(`TaskPerformancesTable`)을 사이드패널 안에서 보여주는 래퍼.
 *
 * 필터: 패널 상단에 ListPanelFilterBar(사업/부서/담당자/검색 — 성과는 상태 미지원).
 */

import React, { useEffect, useState } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import TaskPerformancesTable from './TaskPerformancesTable'
import ListPanelFilterBar from './ListPanelFilterBar'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'

export interface TaskPerformancesListPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 사이드패널 헤더 타이틀. 기본값: "성과 목록" */
  title?: string
  /** 사이드패널 너비. 기본 1000 */
  width?: number
  /** z-index. 기본 40 (상세 패널이 위에 뜨도록 detail의 기본 50보다 낮춤) */
  zIndex?: number

  // ===== 사전 적용 필터 (lock 됨) =====
  workspaceId: string
  selectedBusinessId?: string
  selectedBusinessName?: string
  selectedDepartmentId?: string
  selectedDepartmentName?: string
  selectedMemberId?: string
  selectedMemberName?: string
  startDate?: Date | null
  endDate?: Date | null
  searchQuery?: string
  searchTrigger?: number
  /** 'incomplete' | 'completed' — 정의되면 lock */
  status?: string
  isActionButtonClicked?: boolean

  // ===== 콜백 =====
  onPerformanceItemClick?: (taskId: string) => void
  onReportClick?: (taskItem: TaskTreeNode) => void
  /** 값이 바뀌면 목록을 다시 조회한다 */
  refreshTrigger?: number
}

const TaskPerformancesListPanel: React.FC<TaskPerformancesListPanelProps> = ({
  isOpen,
  onClose,
  title = '성과 목록',
  width = 1000,
  zIndex = 40,
  workspaceId,
  selectedBusinessId,
  selectedBusinessName,
  selectedDepartmentId,
  selectedDepartmentName,
  selectedMemberId,
  selectedMemberName,
  startDate,
  endDate,
  searchQuery,
  searchTrigger,
  status,
  isActionButtonClicked,
  onPerformanceItemClick,
  onReportClick,
  refreshTrigger,
}) => {
  const [panelStatus, setPanelStatus] = useState('')
  const [panelBusinessId, setPanelBusinessId] = useState('')
  const [panelBusinessName, setPanelBusinessName] = useState('')
  const [panelDepartmentId, setPanelDepartmentId] = useState('')
  const [panelDepartmentName, setPanelDepartmentName] = useState('')
  const [panelMemberId, setPanelMemberId] = useState('')
  const [panelMemberName, setPanelMemberName] = useState('')
  const [searchInput, setSearchInput] = useState(searchQuery || '')
  const [panelSearchQuery, setPanelSearchQuery] = useState(searchQuery || '')
  const [panelSearchTrigger, setPanelSearchTrigger] = useState(0)

  useEffect(() => {
    if (!isOpen) {
      setPanelStatus('')
      setPanelBusinessId('')
      setPanelBusinessName('')
      setPanelDepartmentId('')
      setPanelDepartmentName('')
      setPanelMemberId('')
      setPanelMemberName('')
      setSearchInput(searchQuery || '')
      setPanelSearchQuery(searchQuery || '')
    }
  }, [isOpen, searchQuery])

  const handleSearchSubmit = () => {
    setPanelSearchQuery(searchInput.trim())
    setPanelSearchTrigger(t => t + 1)
  }

  const effectiveStatus = status ?? (panelStatus || undefined)
  const effectiveBusinessId = selectedBusinessId ?? (panelBusinessId || undefined)
  const effectiveDepartmentId = selectedDepartmentId ?? (panelDepartmentId || undefined)
  const effectiveMemberId = selectedMemberId ?? (panelMemberId || undefined)

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={title} width={width} zIndex={zIndex}>
      <div className="flex flex-col h-full">
        <ListPanelFilterBar
          workspaceId={workspaceId}
          showStatus
          statusOptions={[
            { value: 'incomplete', label: '미완료' },
            { value: 'completed', label: '완료' },
          ]}
          lockedStatus={status}
          status={panelStatus}
          onStatusChange={setPanelStatus}
          lockedBusinessId={selectedBusinessId}
          lockedBusinessName={selectedBusinessName}
          businessId={panelBusinessId}
          businessName={panelBusinessName}
          onBusinessChange={(id, name) => { setPanelBusinessId(id); setPanelBusinessName(name) }}
          lockedDepartmentId={selectedDepartmentId}
          lockedDepartmentName={selectedDepartmentName}
          departmentId={panelDepartmentId}
          departmentName={panelDepartmentName}
          onDepartmentChange={(id, name) => { setPanelDepartmentId(id); setPanelDepartmentName(name) }}
          lockedMemberId={selectedMemberId}
          lockedMemberName={selectedMemberName}
          memberId={panelMemberId}
          memberName={panelMemberName}
          onMemberChange={(id, name) => { setPanelMemberId(id); setPanelMemberName(name) }}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearchSubmit={handleSearchSubmit}
        />
        <div className="p-4 flex-1 overflow-hidden">
          <TaskPerformancesTable
            refreshTrigger={refreshTrigger}
            workspaceId={workspaceId}
            selectedBusinessId={effectiveBusinessId}
            selectedDepartmentId={effectiveDepartmentId}
            selectedMemberId={effectiveMemberId}
            isVisible={isOpen}
            startDate={startDate}
            endDate={endDate}
            searchQuery={panelSearchQuery}
            searchTrigger={(searchTrigger || 0) + panelSearchTrigger}
            status={effectiveStatus}
            isActionButtonClicked={isActionButtonClicked}
            onPerformanceItemClick={onPerformanceItemClick}
            onReportClick={onReportClick}
          />
        </div>
      </div>
    </SidePanel>
  )
}

export default TaskPerformancesListPanel
